import { LocalNotifications } from "@capacitor/local-notifications";
import { hashStringToInt } from "../helpers/hash";
import { Preferences } from "@capacitor/preferences";
import calculateDeadlineNotificationTimes from "./calculateDeadlineNotificationTimes";
import {
  getNotificationPreferences,
  isNotificationTypeEnabled,
  shiftOutOfQuietHours,
} from "./notificationPreferences";

const scheduleDeadlineNotificationsForTask = async (task, createdTimestamp) => {
  // Check user notification preferences
  const prefs = await getNotificationPreferences();
  const deadlineApproachingEnabled = isNotificationTypeEnabled(
    prefs,
    "taskReminders",
    "deadlineApproaching",
  );
  const overdueEnabled = isNotificationTypeEnabled(
    prefs,
    "taskReminders",
    "overdue",
  );

  // If both are disabled, skip entirely
  if (!deadlineApproachingEnabled && !overdueEnabled) {
    return;
  }

  //Check if task already has notifications scheduled
  const existingNotifications = await Preferences.get({
    key: `task_${task.taskId}_notifications_deadline`,
  });
  if (existingNotifications.value) {
    return;
  }

  const { nearlyDue, overdue } = calculateDeadlineNotificationTimes(
    createdTimestamp ?? task.created,
    task.deadline,
  );

  const now = Date.now();
  const isNearlyDueInFuture = nearlyDue.getTime() > now;
  const isOverdueInFuture = overdue.getTime() > now;

  // Skip entirely if both notification times are in the past
  if (!isNearlyDueInFuture && !isOverdueInFuture) {
    return;
  }

  let overdueNotificationId = null;
  let nearlyDueNotificationId = null;

  // Schedule overdue notification (only if enabled AND in the future)
  if (overdueEnabled && isOverdueInFuture) {
    console.log("Scheduling deadline notifications for task:", task.taskId);
    overdueNotificationId = hashStringToInt(`${task.taskId}-deadline`);
    const overdueTime = shiftOutOfQuietHours(overdue, prefs.quietHours);
    await LocalNotifications.schedule({
      notifications: [
        {
          title: "Task overdue",
          body: `"${task.name}" is now overdue`,
          id: overdueNotificationId,
          schedule: { at: overdueTime, allowWhileIdle: true },
          extra: task.taskId,
          channelId: "task_notifications",
        },
      ],
    });
    console.log(
      "Scheduled overdue deadline notification for task:",
      task.taskId,
    );
  }

  // Schedule notification when 90% of time between creation and deadline has passed
  // (only if enabled AND in the future)
  if (deadlineApproachingEnabled && isNearlyDueInFuture) {
    nearlyDueNotificationId = hashStringToInt(
      `${task.taskId}-deadline-approaching`,
    );
    const nearlyDueTime = shiftOutOfQuietHours(nearlyDue, prefs.quietHours);
    await LocalNotifications.schedule({
      notifications: [
        {
          title: "Task deadline approaching",
          body: `"${task.name}" is due soon`,
          id: nearlyDueNotificationId,
          schedule: { at: nearlyDueTime, allowWhileIdle: true },
          extra: task.taskId,
          channelId: "task_notifications",
        },
      ],
    });
    console.log(
      "Scheduled deadline approaching notification for task:",
      task.taskId,
    );
  }

  // Update preferences with notification ids
  await Preferences.set({
    key: `task_${task.taskId}_notifications_deadline`,
    value: JSON.stringify({
      nearlyDueNotificationId,
      overdueNotificationId,
    }),
  });
};

export default scheduleDeadlineNotificationsForTask;
